/**
 * Texte für alle Anlass-Landingpages, gepflegt pro Sprache.
 *
 * Bewusst keine Zahlen zu Event-Mengen oder Teilnehmer-Durchschnitten —
 * datensparsam und ohne Rhetorik, die nach „große Datenbank" klingt.
 * Die Beispiele sind generische Varianten, keine Verweise auf konkrete
 * Ereignisse der Vergangenheit.
 */

export type UseCaseContent = {
  shortLabel: string;
  title: string;
  tagline: string;
  intro: string;
  typical: string; // ein Satz: „Meistens 15–40 Gäste"
  examples: string[];
  itemIdeas: string[];
  tips: string[];
};

type ContentByLang = Record<string, Record<string, UseCaseContent>>;

const DE: Record<string, UseCaseContent> = {
  hochzeit: {
    shortLabel: "Hochzeit",
    title: "Hochzeit organisieren",
    tagline: "Sektempfang, Häppchen, Deko — Aufgaben unter Trauzeug*innen und Gästen klar verteilen.",
    intro:
      "Gerade der Sektempfang vor dem Standesamt — mit selbstgemachten Häppchen — lässt sich so in wenigen Minuten koordinieren, ohne dass drei Leute Fingerfood doppelt machen.",
    typical: "Meistens zwischen 15 und 40 beitragenden Personen.",
    examples: [
      "Sektempfang vor dem Standesamt mit Häppchen",
      "Fingerfood-Platten fürs Standesamt",
      "After-Standesamt-Party mit Polterabend",
      "Hochzeitsfeier im kleinen Kreis",
      "Dessert-Buffet für große Hochzeit",
      "Hochzeits-Brunch am Tag danach",
    ],
    itemIdeas: [
      "Häppchen für Sektempfang",
      "Bruschetta / Canapés",
      "Mini-Quiches",
      "Obstspieße",
      "Käseplatte",
      "Antipasti",
      "Dessert / Cupcakes",
      "Getränke (Sekt, Wasser, alkoholfrei)",
      "Servietten, Teller, Sektgläser",
      "Blumen / Tischdeko",
    ],
    tips: [
      "Deadline zwei bis drei Tage vor der Hochzeit setzen, damit am Abend vorher nichts mehr fehlt.",
      "Trauzeug*innen als Admin-Link weiterleiten — dann können sie zuordnen und erinnern.",
      "Für alle-Items (z. B. Getränke, Deko) markieren, damit klar ist: das ist zentral gestellt.",
    ],
  },

  geburtstag: {
    shortLabel: "Geburtstag",
    title: "Geburtstag organisieren",
    tagline: "Vom Kindergeburtstag bis zum Runden — jede*r bringt etwas mit.",
    intro:
      "Ob 30er, 40er, 50er oder der Kindergeburtstag mit sechs Kindern: jede*r kann ein Item übernehmen, und alle sehen auf einen Blick, was noch offen ist.",
    typical: "Meistens zwischen 6 und 20 Gästen.",
    examples: [
      "Runder Geburtstag im Freundeskreis",
      "Großer Geburtstag mit der Familie",
      "Kindergeburtstag — Kinder + Eltern",
      "Geburtstagsbrunch im Garten",
      "Kollegen-Umtrunk im Büro",
      "Gemeinsamer Doppel-Geburtstag zweier Freund*innen",
    ],
    itemIdeas: [
      "Kuchen / Torte",
      "Salate (Kartoffel, Nudel, Couscous)",
      "Quiche / Pizza",
      "Dips & Brot",
      "Chips & Knabberei",
      "Getränke",
      "Eis / Dessert",
      "Deko / Luftballons",
      "Geschirr, Servietten",
    ],
    tips: [
      "Link früh teilen — Geschenke brauchen Zeit.",
      "Bei Kindergeburtstagen: eine Position für Kuchen + eine für Geschenkideen anlegen und beide teilen.",
      "Auch nach der Party aktiv lassen — manchmal bringt jemand was vom Rest nach.",
    ],
  },

  grillen: {
    shortLabel: "Grillen",
    title: "Grillabend & Grillfeste organisieren",
    tagline: "Wer bringt Fleisch, wer Salat, wer die Kohle? Eine Liste statt zehn Nachrichten.",
    intro:
      "Vom spontanen Grillabend im Freundeskreis bis zum Spanferkelgrillen im größeren Kreis: eine gemeinsame Liste verhindert, dass drei Leute Würstchen bringen und keiner an Brot denkt.",
    typical: "Meistens zwischen 8 und 20 Gästen.",
    examples: [
      "Spanferkelgrillen im größeren Kreis",
      "Sommer-Grillparty im Freundeskreis",
      "Grillen zum Schuljahresabschluss",
      "Abschluss-Grillen im Büro / Abteilung",
      "Raclette-Abend als Indoor-Variante",
      "Privates Grillen im Garten",
    ],
    itemIdeas: [
      "Bratwurst / Steaks / Spieße",
      "Veggie-Würstchen / Halloumi / Grillkäse",
      "Brot & Brötchen",
      "Kartoffelsalat (ohne Mayo)",
      "Nudelsalat",
      "Grüner Salat",
      "Dips & Saucen (Ketchup, Senf, Tzatziki)",
      "Getränke (Bier, Wein, alkoholfrei)",
      "Grill-Kohle / Anzünder",
      "Dessert / Obst",
    ],
    tips: [
      "Getrenntes Fleisch und Veggie anlegen — Vegetarier*innen finden so direkt ihre Position.",
      "Für alle-Position für Brot & Getränke: meist bringt da einer den ganzen Einkauf.",
      "Grill-Zubehör (Kohle, Zange) als eigene Positionen anlegen, sonst wird es vergessen.",
    ],
  },

  brunch: {
    shortLabel: "Brunch",
    title: "Brunch & Frühstück in der Gruppe",
    tagline: "Croissants, Käseplatte, Obst — alle bringen was, einer spart sich das Einkaufen.",
    intro:
      "Ob Sonntagmorgen mit der Clique, Rosenmontagsbrunch oder Klassen-Frühstück in der Schule — eine gemeinsame Liste verteilt Beiträge ohne Chat-Chaos.",
    typical: "Meistens zwischen 6 und 20 Personen.",
    examples: [
      "Sonntagsbrunch im Freundeskreis",
      "Frühstück in der Schulklasse / am Klausurtag",
      "Rosenmontags- oder Gumpendonnerstags-Frühstück",
      "Weihnachtsfrühstück im Team",
      "JGA-Brunch am Morgen danach",
      "Oster-Brunch im Familienkreis",
    ],
    itemIdeas: [
      "Brötchen / Brot",
      "Aufschnitt / Käse",
      "Rührei / Bacon / Frühstücksspeck",
      "Marmelade / Honig / Nutella",
      "Obstsalat / frisches Obst",
      "Joghurt / Müsli / Granola",
      "Quark / Frischkäse",
      "Croissants / Kuchen",
      "Säfte / Kaffee / Tee",
      "Prosecco / Hugo (wenn es Richtung Mittag geht)",
    ],
    tips: [
      "Früher Start — Brunch beginnt oft um 10 oder 11, Deadline also am Abend vorher.",
      "Eine Position für Kaffee- und Tee-Maschine vergeben — klingt kleinlich, hilft aber.",
      "Resteverwertung: viele Brunches gehen in ein Mittagessen über — einen Rest-Aufbraucher einplanen.",
    ],
  },

  "schule-kita": {
    shortLabel: "Schule & Kita",
    title: "Schul- & Kita-Feste organisieren",
    tagline: "Abschlussfeier, Klassenfest, Kita-Sommerfest — Eltern koordinieren sich selbst.",
    intro:
      "Elternabend, Abschlussfest, Klassen-Frühstück, Kita-Sommerfest — überall dort, wo Eltern gemeinsam etwas beisteuern, verhindert eine geteilte Liste das typische Drei-Nudelsalate-kein-Getränk-Chaos.",
    typical: "Meistens zwischen 12 und 25 Beitragenden.",
    examples: [
      "Klassenfest in der Grundschule",
      "Abschlussfest 4. Klasse / 10. Klasse",
      "Einschulungsfeier",
      "Klassentreffen nach Jahren",
      "Sommerfest der Kita",
      "St.-Martinsfest im Kindergarten",
      "Elternsprechtags-Buffet",
    ],
    itemIdeas: [
      "Kuchen / Muffins",
      "Herzhaftes (Laugenbrezeln, Pizza, Quiche)",
      "Salate",
      "Obstspieße für Kinder",
      "Gekochte Würstchen / Brötchen",
      "Saft / Apfelschorle / Wasser",
      "Kaffee für Eltern",
      "Geschirr / Servietten (oft Schule)",
    ],
    tips: [
      "Eltern stecken oft in mehreren Chatgruppen — ein Link-Teilen statt drei Gruppen-Nachrichten spart Streit.",
      "Bei Allergien eine extra Hinweis-Spalte nutzen (vegan, nussfrei, glutenfrei).",
      "Admin-Rolle an Klassenlehrer*in oder Elternsprecher*in übergeben — so bleibt die Orga nicht am Einzelnen hängen.",
    ],
  },

  helferliste: {
    shortLabel: "Helferliste",
    title: "Helferliste & Schichtplan",
    tagline: "Sommerturnier, Dorffest, Bewirtung — wer übernimmt welche Schicht?",
    intro:
      "Für Vereinsveranstaltungen wie Sommerturnier, Maifest, Kuchenverkauf oder Bewirtung lässt sich die geteilte Liste als reine Anmeldeliste nutzen: statt Mitbringsel steht dort Schicht oder Aufgabe — das Prinzip ist dasselbe.",
    typical: "Meistens zwischen 15 und 35 Helfer*innen.",
    examples: [
      "Dienstplan für Verkauf, Spülmobil, Abbau",
      "Schichtplan für den 1. Mai oder Maibaumfest",
      "Schichten beim Sommerturnier über mehrere Tage",
      "Bezirks- oder Gaumeisterschaft Bewirtung",
      "Vereins-Sommerfest Kuchen + Bewirtung",
      "Musikverein Bewirtung bei Konzert",
    ],
    itemIdeas: [
      "Schicht Vormittag (z. B. 9–12 Uhr)",
      "Schicht Nachmittag (12–15 Uhr)",
      "Schicht Abend / Spülmobil",
      "Auf- und Abbau",
      "Grillstand",
      "Getränkestand / Kasse",
      "Kuchentheke",
      "Kinderbetreuung",
    ],
    tips: [
      "Jede Schicht als eigenes Item anlegen, idealerweise mit Datum und Uhrzeit im Namen.",
      "Admin-Link an Hauptorganisator*in weitergeben — die kann bei Engpässen umverteilen.",
      "Für Doppelbesetzungen: Item duplizieren und ein zweites für die zweite Person anbieten.",
    ],
  },

  kuchenspende: {
    shortLabel: "Kuchenspende",
    title: "Kuchenspende & Speisenspenden",
    tagline: "Schulfest, Sportfest, Dorffest — wer bringt welchen Kuchen, welchen Salat?",
    intro:
      "Klassischer Anwendungsfall für Schulen, Vereine und Kirchen: eine öffentliche Veranstaltung braucht Kuchen oder Salate für den Verkaufsstand. Die Liste zeigt, was schon zugesagt ist — und verhindert, dass alle Schokokuchen backen.",
    typical: "Meistens zwischen 15 und 40 Beitragenden.",
    examples: [
      "Kuchenspende beim Schulfest",
      "Kuchenspende beim Dorffest",
      "Salat- oder Kuchenspende für Sportmeisterschaft",
      "Kuchentheke beim Turnier",
      "Kuchenverkauf des Fördervereins",
    ],
    itemIdeas: [
      "Rührkuchen / Marmorkuchen",
      "Obstkuchen saisonal",
      "Muffins (2 Dutzend)",
      "Torte mit Cremeschicht",
      "Veganer Kuchen",
      "Kuchen ohne Nüsse",
      "Glutenfrei",
      "Kaffee-Thermoskannen",
      "Einwegteller / Servietten",
    ],
    tips: [
      "Sorten-Vielfalt: konkrete Sorten eintragen (Apfel, Marmor) — so werden Doppelungen sichtbar.",
      "Allergie-Hinweise in die Kommentarspalte — vegan, glutenfrei, nussfrei.",
      "Eine Position für Kaffee & Kannen vergeben — wird oft vergessen.",
    ],
  },

  weihnachten: {
    shortLabel: "Weihnachten",
    title: "Weihnachten organisieren",
    tagline: "Weihnachtsfeier, Plätzchen-Backen, Wichtelliste — alle Fäden an einem Ort.",
    intro:
      "Firmen-Weihnachtsfeier, Klassen-Adventsfeier, gemeinsames Plätzchen-Backen oder die Familien-Wichtelrunde: eine geteilte Liste organisiert das Durcheinander typischer Dezember-Wochen.",
    typical: "Meistens zwischen 10 und 25 Personen.",
    examples: [
      "Firmen- oder Vereins-Weihnachtsfeier",
      "Klassen-Weihnachtsfeier",
      "Weihnachtsfrühstück im Team oder in der Klasse",
      "Gemeinsames Plätzchen-Backen",
      "Adventsaktion oder Vorweihnachtliches Hocketse",
      "Bewirtung beim Weihnachtskonzert oder -markt",
      "Wichtel- oder Wunschzettel im Familienkreis",
    ],
    itemIdeas: [
      "Plätzchen-Sorten",
      "Glühwein / Kinderpunsch",
      "Lebkuchen / Stollen",
      "Raclette-Zutaten",
      "Fondue-Brühe / -Fleisch",
      "Käse / Antipasti",
      "Kartoffelsalat (Heiligabend-Klassiker)",
      "Würstchen",
      "Wichtelgeschenke-Ideen",
    ],
    tips: [
      "Die Wichtelliste ist ein unterschätzter Anwendungsfall: jeder trägt ein bis drei Wunschideen in seine Position ein — Doppelungen ausgeschlossen.",
      "Plätzchen-Backen: jede Familie bringt einen Teig plus eigene Formen.",
      "Weihnachtsfeier Firma: Admin-Link an HR oder Office-Management, damit jemand zentral nachhält.",
    ],
  },

  silvester: {
    shortLabel: "Silvester",
    title: "Silvesterparty organisieren",
    tagline: "Snacks, Fondue, Raketen, Sekt — wer bringt was zum Jahreswechsel?",
    intro:
      "Silvesterfeiern sind oft spontan und mit wechselnden Gästen: die Liste hilft der Gastgeberin, den Abend nicht mit einem Kühlschrank voll Dips und ohne Sekt zu beginnen.",
    typical: "Meistens zwischen 6 und 15 Gästen.",
    examples: [
      "Silvester im Freundeskreis",
      "Silvester-Fondue zu Hause",
      "Silvesterparty mit Live-Musik oder Motto",
      "Silvester in der Hütte oder mit Anreise",
      "Neujahrsbrunch am 1. Januar",
    ],
    itemIdeas: [
      "Snacks (Chips, Nüsse, Knabberei)",
      "Fondue-Brühe / Fleisch / Brot",
      "Raclette-Käse",
      "Kartoffeln / Gemüse für Raclette",
      "Dips & Saucen",
      "Berliner / Krapfen",
      "Sekt / Prosecco",
      "Feuerwerk / Wunderkerzen",
      "Bleigießen / Glücksbringer",
    ],
    tips: [
      "Deadline am 28. Dezember — dann kann der Gastgeber noch einkaufen, was fehlt.",
      "Pfand-Flaschen als eigenes Item: wer bringt Leergut zurück?",
      "Frühstück für den 1. Januar direkt mit einplanen — braucht am Morgen danach keiner mehr organisieren.",
    ],
  },

  jga: {
    shortLabel: "JGA",
    title: "JGA & Polterabend organisieren",
    tagline: "Snacks für unterwegs, Deko, Spiele — das Trauzeug*innen-Team koordiniert sich.",
    intro:
      "JGA und Polterabend sind typisch für geteilte Listen: kleiner Organisationskreis (Trauzeug*innen), viele helfende Freund*innen — jede*r steuert was bei, niemand steht zweimal mit Sektgläsern da.",
    typical: "Meistens zwischen 8 und 15 Gästen.",
    examples: [
      "Polterabend",
      "JGA-Brunch am Morgen",
      "Picknick oder Tagesausflug als JGA",
      "After-Standesamt-Party mit Polterabend",
      "JGA-Snacks / Fingerfood",
    ],
    itemIdeas: [
      "Fingerfood / Häppchen",
      "Snacks für unterwegs",
      "Getränke (Sekt, Bier, Wasser)",
      "Deko / Ballons / Schärpen",
      "Spiele / Aktivitäten",
      "Geschirr / Einweg-Becher",
      "Fotokamera / Polaroid",
      "Scherben bringen Glück (Polterabend)",
    ],
    tips: [
      "Admin-Link bei den Trauzeug*innen — nicht beim Brautpaar, sonst wird es Spoilen.",
      "Spiele & Aktivitäten auch als Item mit Kommentar anlegen — so weiß jeder, was mitzubringen ist.",
      "JGA-Morgen-Brunch in eigener Liste anlegen (nächster Tag, andere Crew eventuell).",
    ],
  },

  dorffest: {
    shortLabel: "Dorf- & Sommerfest",
    title: "Dorf-, Sommer- und Straßenfeste",
    tagline: "Kuchen, Salat, Bewirtung — viele Hände für eine gemeinsame Tafel.",
    intro:
      "Bei Dorf-, Sommer- und Straßenfesten organisieren sich Nachbarschaften, Kirchengemeinden oder Vereine mit einer geteilten Liste. Typisch: Haushalte bringen je einen Beitrag zur gemeinsamen Tafel.",
    typical: "Meistens zwischen 15 und 35 Beitragenden.",
    examples: [
      "Sommerfest Verein, Chor oder Kita",
      "Dorffest Kuchenspende",
      "Straßenfest in der Nachbarschaft",
      "Maibaumfest oder 1. Mai",
      "Oktoberfest im Verein",
    ],
    itemIdeas: [
      "Kuchen (mehrere Sorten)",
      "Salate (Kartoffel, Nudel, grün)",
      "Gegrilltes / Fleisch",
      "Brot / Brezen",
      "Dips & Brote",
      "Getränke für Verkauf / Ausschank",
      "Geschirr / Einwegbecher",
      "Auf- und Abbau",
    ],
    tips: [
      "Bei Fundraising: die Liste gleichzeitig als Preisauszeichnung nutzen (Kuchen 3 €/Stück).",
      "Kassen- und Ausschank-Schichten als eigene Positionen anlegen.",
      "Foto vom Buffet am Ende — für das nächste Fest eine Vorlage.",
    ],
  },

  camping: {
    shortLabel: "Camping & Hütte",
    title: "Camping-Wochenende & Hüttenurlaub",
    tagline: "Zelt, Topf, Proviant — wer bringt was mit in die Berge oder an den See?",
    intro:
      "Hüttenwochenenden, Camping-Trips und Wander-Wochenenden sind kleine, dichte Runden. Gepäck ist begrenzt — eine klare Liste verhindert, dass drei Töpfe und null Kaffee mitreisen.",
    typical: "Meistens zwischen 4 und 10 Personen.",
    examples: [
      "Wander-Wochenende in den Bergen",
      "Hüttengaudi im Winter / Skifreizeit",
      "Camping-Wochenende mit Familien",
      "Jugendgruppen-Hüttenfreizeit",
    ],
    itemIdeas: [
      "Zelt(e) / Isomatten",
      "Schlafsack extra",
      "Kocher / Gas",
      "Topf / Pfanne",
      "Kaffee-Kanne / French Press",
      "Grundnahrungsmittel (Pasta, Reis, Öl)",
      "Frühstück (Müsli, Brot, Nutella)",
      "Snacks für Wanderungen",
      "Stirnlampe / Erste-Hilfe-Set",
      "Spiele (Karten, Brett)",
    ],
    tips: [
      "Kategorien Ausrüstung und Essen klar trennen, sonst kauft jemand zusätzlich ein, was schon mitreist.",
      "Für Wanderungen mit mehreren Etappen: einzelne Tage als separate Positionen.",
      "Abfall einplanen: Müllsäcke und wer nimmt alles zurück als Item.",
    ],
  },

  "taufe-kommunion": {
    shortLabel: "Taufe & Kommunion",
    title: "Taufe, Kommunion & Konfirmation",
    tagline: "Kirchliche Feiern mit Familie — Geschenkekoordination und Empfang.",
    intro:
      "Feiern im Familien- und Freundeskreis. Besonders beliebt ist die Geschenke-Koordination: jede*r trägt eine Idee ein, keine Dopplungen.",
    typical: "Meistens zwischen 10 und 17 Gästen.",
    examples: [
      "Taufe mit Familie & Paten",
      "Kommunion-Abschlussfest",
      "Konfirmationsbrunch",
      "Geschenkeliste zur Taufe oder Konfirmation",
    ],
    itemIdeas: [
      "Buffet-Beiträge (Salate, Kuchen, Antipasti)",
      "Sekt oder alkoholfreier Empfangsdrink",
      "Kaffee & Kuchen am Nachmittag",
      "Deko / Kerzen / Blumen",
      "Gästebuch",
      "Geschenkideen (Kinderbuch, Silberkette, Spiele …)",
    ],
    tips: [
      "Geschenkliste als eigenes Event anlegen — getrennt von der Bewirtung.",
      "Oft mit drei Generationen am Tisch: einfache Bedienung ist wichtig, Admin-Link an die Orga.",
      "Kommentar-Funktion für Allergien (oft Großeltern dabei).",
    ],
  },

  karneval: {
    shortLabel: "Karneval",
    title: "Karneval, Fasching & Fastnacht",
    tagline: "Umzug, Party, Brunch — wer bringt welches Kostüm und welches Buffet-Teil?",
    intro:
      "Karnevals- und Faschings-Feiern laufen oft über mehrere Tage mit wechselnden Gruppen. Besonders hilfreich bei den Frühstücks-Runden vor dem Umzug und dem Rosenmontags-Brunch.",
    typical: "Meistens zwischen 10 und 22 Personen.",
    examples: [
      "Gumpendonnerstag-Frühstück",
      "Karnevals- oder Rosenmontags-Brunch",
      "Vor-Umzug-Treffen",
      "Kindergarten-Faschings-Frühstück",
    ],
    itemIdeas: [
      "Kostüm-Accessoires zum Tauschen",
      "Frühstücks-Brötchen / Brot",
      "Eierlikör / Rosenmontags-Snacks",
      "Krapfen / Berliner",
      "Kinderpunsch / Glühwein (rheinisch)",
      "Kamelle-Sammeltüten",
    ],
    tips: [
      "Mehrere Tage — mehrere Listen, oder ein Event mit Datumsangaben in den Items.",
      "Für Umzugsteilnehmende: getrennte Position vor dem Umzug und nach dem Umzug.",
      "Ab mittags wird es feucht-fröhlich: Deadline früh genug (Karnevals-Sonntag).",
    ],
  },

  einweihung: {
    shortLabel: "Einweihung",
    title: "Einzugs- & Einweihungsparty",
    tagline: "Neues Zuhause, leere Regale, Gäste mit Hunger — klare Sache für eine Mitbringliste.",
    intro:
      "Hauseinweihung, Wohnungseinstand, erste Party im neuen WG-Zimmer: Küche ist oft noch halb ausgepackt, Gäste bringen Salate, Getränke, manchmal auch Deko.",
    typical: "Meistens zwischen 6 und 15 Gästen.",
    examples: [
      "WG- oder Wohnungseinweihung",
      "Hauseinweihung im Freundeskreis",
      "Einstand beim neuen Team oder Job",
    ],
    itemIdeas: [
      "Salate & Dips",
      "Pizza / Quiche",
      "Knabberei",
      "Getränke (Wein, Bier)",
      "Einweihungsgeschenke (Pflanze, Brot & Salz, Gewürzset)",
      "Musik-Box",
      "Geschirr (Küche noch nicht komplett)",
    ],
    tips: [
      "Gäste-Geschenke als Position anlegen: so ist das Regal nicht dreimal voll Kerzen.",
      "Eine Position für Aufräumen am nächsten Morgen — nur für die engsten Freund*innen.",
    ],
  },

  familienfest: {
    shortLabel: "Familienfest",
    title: "Familienfeste & Jubiläen",
    tagline: "Drei Generationen, eine Liste — Uralt-Rezepte und moderne Technik in Harmonie.",
    intro:
      "Familienfeste von Oma-Opa-Geburtstagen über Familientreffen bis zu Jubiläen haben oft viele Beitragende aus mehreren Haushalten. Die Liste hilft besonders, wenn die Familie übers ganze Land verstreut lebt.",
    typical: "Meistens zwischen 20 und 45 Personen.",
    examples: [
      "Oma-Opa-Fest oder Goldene Hochzeit",
      "Familientreffen über drei Generationen",
      "Runder Geburtstag mit erweiterter Familie",
    ],
    itemIdeas: [
      "Uralt-Rezepte (Omas Kartoffelsalat)",
      "Kuchen & Torten",
      "Herzhaftes Buffet",
      "Getränke für alle Altersgruppen",
      "Deko und Foto-Wand (früher & heute)",
      "Musik-Playlist mit Wunsch-Liedern",
    ],
    tips: [
      "Senior*innen den Admin-Link per Mail oder auf Papier — nicht jeder ist in der Whatsapp-Gruppe.",
      "Traditions-Rezepte als eigene Items benennen, damit sie nicht verloren gehen.",
      "Extra Position für Foto-Sammlung für die Wand — zieht alle ins Gemeinsame.",
    ],
  },
};

// EN — kompakte Übersetzung mit denselben Strukturen.
const EN: Record<string, UseCaseContent> = {
  hochzeit: {
    shortLabel: "Wedding",
    title: "Organize a wedding",
    tagline: "Cocktail reception, canapés, decor — divide tasks between bridesmaids and guests.",
    intro:
      "Especially the cocktail reception before the registry office — with home-made canapés — can be coordinated in minutes without three people making the same finger food.",
    typical: "Usually between 15 and 40 contributors.",
    examples: [
      "Cocktail reception before the registry office",
      "Canapé platters for the registry office",
      "After-party with polter evening",
      "Intimate wedding celebration",
      "Dessert buffet for a large wedding",
      "Wedding brunch the day after",
    ],
    itemIdeas: [
      "Canapés for the cocktail reception",
      "Bruschetta / small bites",
      "Mini quiches",
      "Fruit skewers",
      "Cheese platter",
      "Antipasti",
      "Dessert / cupcakes",
      "Drinks (sparkling wine, water, non-alcoholic)",
      "Napkins, plates, flutes",
      "Flowers / table decor",
    ],
    tips: [
      "Set a deadline two or three days before the wedding so nothing is missing the night before.",
      "Forward the admin link to the bridesmaids — they can assign and remind.",
      "Mark 'for everyone' items (drinks, decor) so it's clear that these are provided centrally.",
    ],
  },
  geburtstag: {
    shortLabel: "Birthday",
    title: "Organize a birthday",
    tagline: "From kids' parties to round numbers — everyone brings something.",
    intro:
      "Whether a 30th, 40th, 50th or a kids' party with six children: everyone can claim one item and everyone can see at a glance what's still open.",
    typical: "Usually between 6 and 20 guests.",
    examples: [
      "Milestone birthday with friends",
      "Large birthday with family",
      "Kids' birthday with parents",
      "Birthday brunch in the garden",
      "Colleagues' drinks at the office",
      "Joint double birthday of two friends",
    ],
    itemIdeas: [
      "Cake / gateau",
      "Salads (potato, pasta, couscous)",
      "Quiche / pizza",
      "Dips & bread",
      "Chips & snacks",
      "Drinks",
      "Ice cream / dessert",
      "Decorations / balloons",
      "Tableware, napkins",
    ],
    tips: [
      "Share the link early — gifts take time.",
      "For kids' birthdays: one position for the cake and one for gift ideas.",
      "Keep the list active after the party — someone might bring leftovers by.",
    ],
  },
  grillen: {
    shortLabel: "BBQ",
    title: "Organize a barbecue",
    tagline: "Who brings meat, who salad, who the charcoal? One list instead of ten messages.",
    intro:
      "From a spontaneous BBQ with friends to a larger roast: a shared list stops three people bringing sausages with no one thinking of bread.",
    typical: "Usually between 8 and 20 guests.",
    examples: [
      "Large roast with a bigger group",
      "Summer barbecue with friends",
      "End-of-school-year BBQ",
      "Office or department closing BBQ",
      "Raclette night as indoor alternative",
      "Private BBQ in the garden",
    ],
    itemIdeas: [
      "Bratwurst / steaks / skewers",
      "Veggie sausages / halloumi / grill cheese",
      "Bread & rolls",
      "Potato salad (no mayo)",
      "Pasta salad",
      "Green salad",
      "Dips & sauces (ketchup, mustard, tzatziki)",
      "Drinks (beer, wine, non-alcoholic)",
      "Charcoal / firelighters",
      "Dessert / fruit",
    ],
    tips: [
      "Separate meat and veggie positions — vegetarians find their spot directly.",
      "A 'for everyone' position for bread and drinks: usually one person does the entire shop.",
      "Add grill accessories (charcoal, tongs) as their own positions or they'll be forgotten.",
    ],
  },
  brunch: {
    shortLabel: "Brunch",
    title: "Brunch & breakfast in a group",
    tagline: "Croissants, cheese platter, fruit — everyone brings something, one saves the shop.",
    intro:
      "Whether Sunday morning with friends, holiday brunch or class breakfast at school — a shared list splits contributions without chat chaos.",
    typical: "Usually between 6 and 20 people.",
    examples: [
      "Sunday brunch with friends",
      "Class breakfast / exam day breakfast",
      "Holiday or Rosenmontag brunch",
      "Christmas breakfast at work",
      "Hen-party brunch the morning after",
      "Easter brunch with family",
    ],
    itemIdeas: [
      "Rolls / bread",
      "Cold cuts / cheese",
      "Scrambled eggs / bacon",
      "Jam / honey / Nutella",
      "Fruit salad / fresh fruit",
      "Yoghurt / muesli / granola",
      "Cream cheese / quark",
      "Croissants / cake",
      "Juice / coffee / tea",
      "Prosecco / Hugo (if it leans toward lunch)",
    ],
    tips: [
      "Early start — brunch often begins at 10 or 11, so deadline the evening before.",
      "Give one position to whoever runs the coffee machine — sounds petty but it helps.",
      "Brunches often turn into lunch — plan for someone to use up leftovers.",
    ],
  },
  "schule-kita": {
    shortLabel: "School & daycare",
    title: "Organize school and daycare events",
    tagline: "Graduation party, class party, daycare summer fair — parents coordinate themselves.",
    intro:
      "Parents' evenings, graduation parties, class breakfasts, daycare summer fairs — wherever parents contribute together, a shared list prevents the classic three-pasta-salads-no-drinks mess.",
    typical: "Usually between 12 and 25 contributors.",
    examples: [
      "Primary school class party",
      "Graduation party for 4th or 10th grade",
      "Welcome-to-school celebration",
      "Class reunion after many years",
      "Daycare summer fair",
      "St. Martin's festival at kindergarten",
      "Parent-teacher-day buffet",
    ],
    itemIdeas: [
      "Cake / muffins",
      "Savoury (pretzels, pizza, quiche)",
      "Salads",
      "Fruit skewers for kids",
      "Cooked sausages / rolls",
      "Juice / apple spritzer / water",
      "Coffee for parents",
      "Tableware / napkins (often school-provided)",
    ],
    tips: [
      "Parents are often in multiple chat groups — sharing a link is quieter than three group messages.",
      "Use the comment field for allergies (vegan, nut-free, gluten-free).",
      "Give the admin role to the teacher or a parent rep so orga doesn't depend on one person.",
    ],
  },
  helferliste: {
    shortLabel: "Volunteer list",
    title: "Volunteer list & shift plan",
    tagline: "Summer tournament, village fair, catering — who takes which shift?",
    intro:
      "For club events like summer tournaments, May fests, cake sales or catering, the shared list works as a pure sign-up list: instead of contributions it holds shifts or tasks — same principle.",
    typical: "Usually between 15 and 35 volunteers.",
    examples: [
      "Duty roster for sales, wash-up, take-down",
      "Shift plan for May Day or maypole festival",
      "Tournament shifts across several days",
      "District or regional championship catering",
      "Club summer fair cake + catering",
      "Music club catering at concerts",
    ],
    itemIdeas: [
      "Morning shift (e.g. 9am–12pm)",
      "Afternoon shift (12pm–3pm)",
      "Evening shift / wash-up",
      "Setup and take-down",
      "Grill stand",
      "Drinks stand / cashier",
      "Cake counter",
      "Child supervision",
    ],
    tips: [
      "Each shift as its own item, ideally with date and time in the name.",
      "Give the admin link to the main organizer — they can reshuffle when things go short.",
      "For double-staffing: duplicate the item and offer a second slot for a second person.",
    ],
  },
  kuchenspende: {
    shortLabel: "Cake donation",
    title: "Cake and food donations",
    tagline: "School fair, sports event, village fair — who brings which cake, which salad?",
    intro:
      "A classic use case for schools, clubs and churches: a public event needs cake or salads for the stand. The list shows what's already pledged — and stops everyone making chocolate cake.",
    typical: "Usually between 15 and 40 contributors.",
    examples: [
      "Cake donation for school fair",
      "Cake donation for village fair",
      "Salad or cake donation for sports championship",
      "Cake counter at tournament",
      "Cake sale by the parents' club",
    ],
    itemIdeas: [
      "Pound cake / marble cake",
      "Seasonal fruit cake",
      "Muffins (2 dozen)",
      "Layered cream cake",
      "Vegan cake",
      "Nut-free cake",
      "Gluten-free",
      "Coffee flasks",
      "Paper plates / napkins",
    ],
    tips: [
      "Variety matters: enter specific flavours (apple, marble) so duplicates show up.",
      "Allergy notes in the comment field — vegan, gluten-free, nut-free.",
      "Give one position to coffee & flasks — often forgotten.",
    ],
  },
  weihnachten: {
    shortLabel: "Christmas",
    title: "Organize Christmas",
    tagline: "Christmas party, cookie baking, secret Santa — all threads in one place.",
    intro:
      "Office Christmas party, class Advent party, shared cookie baking or a family wish-list exchange: a shared list organizes the chaos of typical December weeks.",
    typical: "Usually between 10 and 25 people.",
    examples: [
      "Office or club Christmas party",
      "Class Christmas party",
      "Christmas breakfast at work or school",
      "Shared cookie baking",
      "Advent event / pre-Christmas gathering",
      "Catering at Christmas concert or market",
      "Secret Santa or wish-list in the family",
    ],
    itemIdeas: [
      "Cookie varieties",
      "Mulled wine / kids' punch",
      "Lebkuchen / stollen",
      "Raclette ingredients",
      "Fondue broth / meat",
      "Cheese / antipasti",
      "Potato salad (Christmas Eve classic)",
      "Sausages",
      "Secret Santa gift ideas",
    ],
    tips: [
      "The wish list is an underrated use case: everyone writes one to three gift ideas — no duplicates.",
      "Cookie baking: each family brings a dough plus their own cutters.",
      "Office Christmas party: admin link to HR or office management for central oversight.",
    ],
  },
  silvester: {
    shortLabel: "New Year's Eve",
    title: "Organize a New Year's Eve party",
    tagline: "Snacks, fondue, fireworks, sparkling wine — who brings what to ring in the year?",
    intro:
      "New Year parties are often spontaneous with changing guests: the list helps the host not to start the evening with a fridge full of dips and no sparkling wine.",
    typical: "Usually between 6 and 15 guests.",
    examples: [
      "New Year with friends",
      "Fondue at home",
      "Themed or live-music New Year party",
      "Cabin or travel New Year",
      "New Year brunch on January 1st",
    ],
    itemIdeas: [
      "Snacks (chips, nuts, nibbles)",
      "Fondue broth / meat / bread",
      "Raclette cheese",
      "Potatoes / veg for raclette",
      "Dips & sauces",
      "Berliners / doughnuts",
      "Sparkling wine / prosecco",
      "Fireworks / sparklers",
      "Lucky charms",
    ],
    tips: [
      "Deadline on December 28th — the host can still shop for what's missing.",
      "Deposit bottles as their own item: who takes the empties back?",
      "Plan breakfast for January 1st directly — no one wants to organize it that morning.",
    ],
  },
  jga: {
    shortLabel: "Hen/stag",
    title: "Organize a hen or stag party",
    tagline: "Snacks on the go, decor, games — the bridesmaids' team coordinates.",
    intro:
      "Hen and stag parties are a classic shared-list case: a small core organizes, many friends help — everyone contributes something, no one ends up with duplicate champagne flutes.",
    typical: "Usually between 8 and 15 guests.",
    examples: [
      "Polter evening",
      "Morning hen brunch",
      "Picnic or day-trip hen",
      "After-registry party with polter",
      "Hen-party snacks / finger food",
    ],
    itemIdeas: [
      "Finger food / canapés",
      "Snacks on the go",
      "Drinks (sparkling wine, beer, water)",
      "Decor / balloons / sashes",
      "Games / activities",
      "Tableware / paper cups",
      "Camera / polaroid",
      "Polter-evening crockery",
    ],
    tips: [
      "Admin link with the bridesmaids — not the couple, or it'll spoil.",
      "Add games and activities as items with comments so everyone knows what to bring.",
      "Put the morning-after brunch in its own list — next day, possibly different crew.",
    ],
  },
  dorffest: {
    shortLabel: "Village / summer fair",
    title: "Village, summer and street festivals",
    tagline: "Cake, salad, catering — many hands for one shared table.",
    intro:
      "Neighbourhoods, church groups or clubs organize village, summer and street festivals with a shared list. Typical: households each bring a contribution to the shared table.",
    typical: "Usually between 15 and 35 contributors.",
    examples: [
      "Club, choir or daycare summer fair",
      "Village fair cake donation",
      "Neighbourhood street festival",
      "Maypole festival or May Day",
      "Oktoberfest in the club",
    ],
    itemIdeas: [
      "Cakes (several varieties)",
      "Salads (potato, pasta, green)",
      "Grilled food / meat",
      "Bread / pretzels",
      "Dips & spreads",
      "Drinks to sell / serve",
      "Tableware / paper cups",
      "Setup and take-down",
    ],
    tips: [
      "For fundraising: the list doubles as the price list (cake 3 €/slice).",
      "Cashier and serving shifts as their own positions.",
      "Photo of the buffet at the end — a template for next time.",
    ],
  },
  camping: {
    shortLabel: "Camping & cabin",
    title: "Camping weekend & cabin holidays",
    tagline: "Tent, pot, provisions — who brings what to the mountains or the lake?",
    intro:
      "Cabin weekends, camping trips and hiking weekends are small, dense groups. Luggage is limited — a clear list prevents three pots and zero coffee from travelling.",
    typical: "Usually between 4 and 10 people.",
    examples: [
      "Hiking weekend in the mountains",
      "Winter cabin or ski trip",
      "Family camping weekend",
      "Youth-group cabin retreat",
    ],
    itemIdeas: [
      "Tent(s) / sleeping mats",
      "Extra sleeping bag",
      "Stove / gas",
      "Pot / pan",
      "Coffee flask / French press",
      "Staples (pasta, rice, oil)",
      "Breakfast (muesli, bread, Nutella)",
      "Snacks for hikes",
      "Head torch / first-aid kit",
      "Games (cards, board)",
    ],
    tips: [
      "Keep equipment and food categories apart, or someone will duplicate the shop.",
      "For multi-day hikes: separate positions per day.",
      "Plan for waste: rubbish bags and who takes them back.",
    ],
  },
  "taufe-kommunion": {
    shortLabel: "Baptism & communion",
    title: "Baptism, communion & confirmation",
    tagline: "Church celebrations with family — gift coordination and reception.",
    intro:
      "Family and friend celebrations. Especially popular is gift coordination: everyone enters an idea — no duplicates.",
    typical: "Usually between 10 and 17 guests.",
    examples: [
      "Baptism with family and godparents",
      "Communion closing celebration",
      "Confirmation brunch",
      "Gift list for baptism or confirmation",
    ],
    itemIdeas: [
      "Buffet contributions (salads, cake, antipasti)",
      "Sparkling or non-alcoholic welcome drink",
      "Coffee and cake in the afternoon",
      "Decor / candles / flowers",
      "Guest book",
      "Gift ideas (kids' book, silver chain, games …)",
    ],
    tips: [
      "Create the gift list as its own event — separate from catering.",
      "Often three generations at the table: easy handling matters, admin link to the organizer.",
      "Comment field for allergies (often grandparents present).",
    ],
  },
  karneval: {
    shortLabel: "Carnival",
    title: "Carnival, Fasching & Mardi Gras",
    tagline: "Parade, party, brunch — who brings which costume and which buffet item?",
    intro:
      "Carnival celebrations often run over several days with rotating groups. Especially useful for the breakfast rounds before the parade and Rosenmontag brunch.",
    typical: "Usually between 10 and 22 people.",
    examples: [
      "Gumpendonnerstag breakfast",
      "Carnival or Rosenmontag brunch",
      "Pre-parade meeting",
      "Kindergarten carnival breakfast",
    ],
    itemIdeas: [
      "Costume accessories to swap",
      "Breakfast rolls / bread",
      "Eggnog / carnival snacks",
      "Doughnuts / Berliner",
      "Kids' punch / mulled wine (Rhine-style)",
      "Bags to collect Kamelle (candy)",
    ],
    tips: [
      "Multiple days — multiple lists, or one event with dates in the items.",
      "For parade-goers: separate positions for before and after the parade.",
      "From midday it gets lively: set the deadline early enough (carnival Sunday).",
    ],
  },
  einweihung: {
    shortLabel: "Housewarming",
    title: "Housewarming & moving-in parties",
    tagline: "New home, empty shelves, hungry guests — clear job for a shared list.",
    intro:
      "Housewarming, flat-warming, first party in the new shared room: kitchen often half-unpacked, guests bring salads, drinks, sometimes decor.",
    typical: "Usually between 6 and 15 guests.",
    examples: [
      "Flat or shared-house warming",
      "House-warming with friends",
      "Welcome to the new team or job",
    ],
    itemIdeas: [
      "Salads & dips",
      "Pizza / quiche",
      "Snacks",
      "Drinks (wine, beer)",
      "Housewarming gifts (plant, bread & salt, spice set)",
      "Music speaker",
      "Tableware (kitchen not yet fully set up)",
    ],
    tips: [
      "Add guest-gifts as positions — avoids three shelves' worth of candles.",
      "One position for helping clean up the next morning — only for your closest friends.",
    ],
  },
  familienfest: {
    shortLabel: "Family gathering",
    title: "Family gatherings & anniversaries",
    tagline: "Three generations, one list — heirloom recipes and modern tech in harmony.",
    intro:
      "Family gatherings — from grandparents' birthdays to reunions and anniversaries — often have many contributors across households. The list especially helps when family is spread across the country.",
    typical: "Usually between 20 and 45 people.",
    examples: [
      "Grandparents' celebration or golden anniversary",
      "Family reunion across three generations",
      "Milestone birthday with extended family",
    ],
    itemIdeas: [
      "Heirloom recipes (grandma's potato salad)",
      "Cakes & gateaux",
      "Savoury buffet",
      "Drinks for all ages",
      "Decor and photo wall (then & now)",
      "Music playlist with requested songs",
    ],
    tips: [
      "Give seniors the admin link by email or on paper — not everyone is in the WhatsApp group.",
      "Name traditional recipes as their own items so they aren't lost.",
      "Extra position for photo collection for the wall — pulls everyone into the shared project.",
    ],
  },
};

// ES — kompakte Übersetzung.
const ES: Record<string, UseCaseContent> = {
  hochzeit: {
    shortLabel: "Boda",
    title: "Organizar una boda",
    tagline: "Cóctel, aperitivos, decoración — reparte tareas entre padrinos e invitados.",
    intro:
      "Sobre todo el cóctel antes del registro civil — con canapés caseros — se coordina en minutos sin que tres personas hagan los mismos aperitivos.",
    typical: "Normalmente entre 15 y 40 participantes.",
    examples: [
      "Cóctel antes del registro civil",
      "Bandejas de canapés para el registro civil",
      "After-party con polterabend",
      "Boda íntima",
      "Buffet de postres para boda grande",
      "Brunch al día siguiente",
    ],
    itemIdeas: [
      "Canapés para el cóctel",
      "Bruschetta / bocaditos",
      "Mini quiches",
      "Brochetas de fruta",
      "Tabla de quesos",
      "Antipasti",
      "Postre / cupcakes",
      "Bebidas (cava, agua, sin alcohol)",
      "Servilletas, platos, copas",
      "Flores / decoración de mesa",
    ],
    tips: [
      "Fecha límite dos o tres días antes para no faltar nada la noche previa.",
      "Pasa el enlace admin a los padrinos — asignan y recuerdan.",
      "Marca los ítems para todos (bebidas, deco) para que quede claro que los aporta la organización.",
    ],
  },
  geburtstag: {
    shortLabel: "Cumpleaños",
    title: "Organizar un cumpleaños",
    tagline: "Del infantil al redondo — cada uno trae algo.",
    intro:
      "Ya sea un 30, 40, 50 o un cumple infantil con seis niños: cada uno se apunta a un ítem y todos ven de un vistazo qué falta.",
    typical: "Normalmente entre 6 y 20 invitados.",
    examples: [
      "Cumpleaños redondo con amigos",
      "Gran cumpleaños en familia",
      "Cumple infantil con padres",
      "Brunch de cumpleaños en el jardín",
      "Copas con compañeros en la oficina",
      "Doble cumpleaños de dos amigas",
    ],
    itemIdeas: [
      "Tarta",
      "Ensaladas (patata, pasta, cuscús)",
      "Quiche / pizza",
      "Dips y pan",
      "Patatas y snacks",
      "Bebidas",
      "Helado / postre",
      "Decoración / globos",
      "Vajilla, servilletas",
    ],
    tips: [
      "Comparte el enlace pronto — los regalos llevan tiempo.",
      "En cumples infantiles: una posición para la tarta y otra para ideas de regalo.",
      "Deja la lista abierta — alguien puede traer sobras.",
    ],
  },
  grillen: {
    shortLabel: "Barbacoa",
    title: "Organizar una barbacoa",
    tagline: "¿Quién lleva carne, quién ensalada, quién el carbón? Una lista en vez de diez mensajes.",
    intro:
      "De la barbacoa espontánea con amigos al cochinillo en grupo grande: una lista compartida evita que tres lleven salchichas y nadie el pan.",
    typical: "Normalmente entre 8 y 20 invitados.",
    examples: [
      "Cochinillo en grupo grande",
      "Barbacoa de verano con amigos",
      "BBQ de fin de curso escolar",
      "BBQ de cierre en la oficina",
      "Noche de raclette como alternativa",
      "Barbacoa privada en el jardín",
    ],
    itemIdeas: [
      "Salchichas / bistecs / brochetas",
      "Salchichas veganas / halloumi / queso para parrilla",
      "Pan y panecillos",
      "Ensaladilla (sin mayonesa)",
      "Ensalada de pasta",
      "Ensalada verde",
      "Dips y salsas (kétchup, mostaza, tzatziki)",
      "Bebidas (cerveza, vino, sin alcohol)",
      "Carbón / pastillas",
      "Postre / fruta",
    ],
    tips: [
      "Carne y veggie separados — los vegetarianos encuentran su sitio.",
      "Una posición para todos para pan y bebidas: alguien hace toda la compra.",
      "Accesorios (carbón, pinzas) como posiciones propias, si no se olvidan.",
    ],
  },
  brunch: {
    shortLabel: "Brunch",
    title: "Brunch y desayuno en grupo",
    tagline: "Croissants, queso, fruta — cada uno trae algo, uno se ahorra la compra.",
    intro:
      "Ya sea un domingo con amigos, un brunch festivo o un desayuno de clase — una lista compartida reparte sin caos.",
    typical: "Normalmente entre 6 y 20 personas.",
    examples: [
      "Brunch de domingo con amigos",
      "Desayuno de clase / día de examen",
      "Brunch de carnaval o Rosenmontag",
      "Desayuno navideño en el equipo",
      "Brunch del día siguiente al JGA",
      "Brunch de Pascua en familia",
    ],
    itemIdeas: [
      "Panecillos / pan",
      "Embutido / queso",
      "Huevos revueltos / bacon",
      "Mermelada / miel / Nutella",
      "Macedonia / fruta fresca",
      "Yogur / muesli / granola",
      "Queso fresco / requesón",
      "Croissants / bizcocho",
      "Zumos / café / té",
      "Prosecco / Hugo (si tira al mediodía)",
    ],
    tips: [
      "Empieza temprano — brunch suele ser a las 10 u 11, fecha límite la noche anterior.",
      "Una posición para quien maneja la cafetera — parece trivial, ayuda.",
      "Los brunch suelen derivar en almuerzo — alguien que use las sobras.",
    ],
  },
  "schule-kita": {
    shortLabel: "Escuela y guardería",
    title: "Organizar fiestas escolares y de guardería",
    tagline: "Fiesta de fin de curso, fiesta de clase, verbena de la guardería — los padres se coordinan.",
    intro:
      "Reuniones de padres, fiestas de fin de curso, desayunos de clase, verbenas de guardería — dondequiera que los padres contribuyan juntos, una lista compartida evita el clásico 'tres ensaladillas y ninguna bebida'.",
    typical: "Normalmente entre 12 y 25 participantes.",
    examples: [
      "Fiesta de clase en primaria",
      "Fiesta de fin de curso (4º, 10º)",
      "Bienvenida al colegio",
      "Reunión de antiguos alumnos",
      "Verbena de guardería",
      "Fiesta de San Martín en guardería",
      "Buffet de jornadas de padres",
    ],
    itemIdeas: [
      "Bizcochos / magdalenas",
      "Salado (pretzel, pizza, quiche)",
      "Ensaladas",
      "Brochetas de fruta para niños",
      "Salchichas cocidas / panecillos",
      "Zumo / agua",
      "Café para padres",
      "Vajilla / servilletas (suele poner la escuela)",
    ],
    tips: [
      "Los padres suelen estar en varios chats — un enlace es menos ruido que tres grupos.",
      "Usa el campo de comentario para alergias (vegano, sin frutos secos, sin gluten).",
      "Da el rol admin al profesor o al delegado — la orga no depende de una sola persona.",
    ],
  },
  helferliste: {
    shortLabel: "Lista de voluntarios",
    title: "Lista de voluntarios y turnos",
    tagline: "Torneo de verano, fiesta del pueblo, catering — ¿quién cubre qué turno?",
    intro:
      "Para eventos de club (torneo, 1º de mayo, venta de tartas, catering), la lista compartida funciona como hoja de inscripción pura: en vez de aportaciones registra turnos o tareas — mismo principio.",
    typical: "Normalmente entre 15 y 35 voluntarios.",
    examples: [
      "Cuadrante de ventas, lavado, desmontaje",
      "Turnos para 1º de mayo o fiesta del maibaum",
      "Turnos de torneo en varios días",
      "Catering de campeonato comarcal o regional",
      "Tarta + catering de fiesta de verano del club",
      "Catering del club musical en conciertos",
    ],
    itemIdeas: [
      "Turno mañana (p. ej. 9–12 h)",
      "Turno tarde (12–15 h)",
      "Turno noche / lavado",
      "Montaje y desmontaje",
      "Parrilla",
      "Barra / caja",
      "Mostrador de tartas",
      "Cuidado de niños",
    ],
    tips: [
      "Cada turno como ítem propio, con fecha y hora en el nombre.",
      "Enlace admin a la organizadora principal — reparte cuando falta gente.",
      "Para doblete: duplica el ítem y ofrece un segundo hueco para otra persona.",
    ],
  },
  kuchenspende: {
    shortLabel: "Donación de tartas",
    title: "Donación de tartas y comida",
    tagline: "Fiesta escolar, evento deportivo, fiesta del pueblo — ¿quién trae qué tarta, qué ensalada?",
    intro:
      "Caso clásico para escuelas, clubes e iglesias: un evento público necesita tartas o ensaladas para el puesto. La lista muestra lo ya prometido — y evita que todos hagan tarta de chocolate.",
    typical: "Normalmente entre 15 y 40 participantes.",
    examples: [
      "Donación de tartas para fiesta escolar",
      "Donación de tartas para fiesta del pueblo",
      "Donación de ensalada o tarta para campeonato deportivo",
      "Mostrador de tartas en torneo",
      "Venta de tartas de la AMPA",
    ],
    itemIdeas: [
      "Bizcocho / bizcocho de mármol",
      "Tarta de fruta de temporada",
      "Magdalenas (2 docenas)",
      "Tarta con crema",
      "Tarta vegana",
      "Tarta sin frutos secos",
      "Sin gluten",
      "Termos de café",
      "Platos de papel / servilletas",
    ],
    tips: [
      "Variedad: indica sabores concretos (manzana, mármol) para ver duplicados.",
      "Alergias en el campo de comentario — vegano, sin gluten, sin frutos secos.",
      "Una posición para café y termos — suele olvidarse.",
    ],
  },
  weihnachten: {
    shortLabel: "Navidad",
    title: "Organizar la Navidad",
    tagline: "Fiesta navideña, hornear galletas, lista de amigo invisible — todos los hilos en un sitio.",
    intro:
      "Fiesta de empresa, adviento en la clase, hornear galletas en común o amigo invisible familiar: una lista compartida organiza el caos de diciembre.",
    typical: "Normalmente entre 10 y 25 personas.",
    examples: [
      "Fiesta de empresa o de club",
      "Fiesta de adviento en la clase",
      "Desayuno navideño en el equipo o la clase",
      "Hornear galletas en común",
      "Acción de adviento",
      "Catering en concierto o mercadillo navideño",
      "Amigo invisible o lista de deseos en familia",
    ],
    itemIdeas: [
      "Variedades de galletas",
      "Vino caliente / ponche infantil",
      "Lebkuchen / stollen",
      "Ingredientes de raclette",
      "Caldo / carne de fondue",
      "Queso / antipasti",
      "Ensaladilla (clásico de Nochebuena)",
      "Salchichas",
      "Ideas de regalo de amigo invisible",
    ],
    tips: [
      "El amigo invisible es un uso subestimado: cada uno escribe una a tres ideas — sin duplicados.",
      "Galletas: cada familia trae una masa y sus moldes.",
      "Fiesta de empresa: enlace admin a RRHH o a la oficina.",
    ],
  },
  silvester: {
    shortLabel: "Nochevieja",
    title: "Organizar fiesta de Nochevieja",
    tagline: "Snacks, fondue, fuegos, cava — ¿quién trae qué para el cambio de año?",
    intro:
      "Las fiestas de fin de año suelen ser espontáneas y con invitados cambiantes: la lista ayuda a no empezar con nevera llena de dips y sin cava.",
    typical: "Normalmente entre 6 y 15 invitados.",
    examples: [
      "Nochevieja con amigos",
      "Fondue en casa",
      "Fiesta temática o con música en vivo",
      "Nochevieja en cabaña o con viaje",
      "Brunch de Año Nuevo el 1 de enero",
    ],
    itemIdeas: [
      "Snacks (patatas, frutos secos)",
      "Caldo de fondue / carne / pan",
      "Queso para raclette",
      "Patatas / verdura para raclette",
      "Dips y salsas",
      "Berlinesas / donuts",
      "Cava / prosecco",
      "Fuegos / bengalas",
      "Amuletos de suerte",
    ],
    tips: [
      "Fecha límite el 28 de diciembre — da margen a comprar lo que falta.",
      "Cascos de botellas como ítem propio: ¿quién devuelve?",
      "Planifica el desayuno del 1 de enero — nadie lo organiza esa mañana.",
    ],
  },
  jga: {
    shortLabel: "Despedida",
    title: "Despedida de soltero/a",
    tagline: "Snacks para el camino, decoración, juegos — el equipo de padrinos se coordina.",
    intro:
      "Despedidas son caso clásico: un núcleo pequeño organiza, muchas amistades ayudan — cada uno contribuye, nadie acaba con copas duplicadas.",
    typical: "Normalmente entre 8 y 15 invitados.",
    examples: [
      "Polterabend",
      "Brunch de mañana de despedida",
      "Picnic o excursión de despedida",
      "After-registro civil con polter",
      "Snacks de despedida",
    ],
    itemIdeas: [
      "Finger food / canapés",
      "Snacks para el camino",
      "Bebidas (cava, cerveza, agua)",
      "Decoración / globos / bandas",
      "Juegos / actividades",
      "Vajilla / vasos de papel",
      "Cámara / polaroid",
      "Vajilla para polter",
    ],
    tips: [
      "Enlace admin con los padrinos — no con los novios, o se estropea.",
      "Apunta juegos y actividades como ítems con comentario.",
      "Lista aparte para el brunch del día siguiente — otro día, otra gente.",
    ],
  },
  dorffest: {
    shortLabel: "Fiesta popular",
    title: "Fiestas del pueblo, de verano y de barrio",
    tagline: "Tarta, ensalada, catering — muchas manos para una mesa compartida.",
    intro:
      "Barrios, parroquias o clubes organizan fiestas del pueblo, de verano y de barrio con una lista compartida. Típico: cada hogar aporta algo a la mesa común.",
    typical: "Normalmente entre 15 y 35 participantes.",
    examples: [
      "Fiesta de verano de club, coro o guardería",
      "Donación de tarta en fiesta del pueblo",
      "Fiesta de barrio",
      "Fiesta de mayo o del maibaum",
      "Oktoberfest en el club",
    ],
    itemIdeas: [
      "Tartas (varias clases)",
      "Ensaladas (patata, pasta, verde)",
      "Parrilla / carne",
      "Pan / pretzels",
      "Dips y untables",
      "Bebidas para vender / servir",
      "Vajilla / vasos de papel",
      "Montaje y desmontaje",
    ],
    tips: [
      "Para recaudación: la lista sirve también como tarifa (tarta 3 €/porción).",
      "Turnos de caja y barra como posiciones propias.",
      "Foto del buffet al final — plantilla para la próxima.",
    ],
  },
  camping: {
    shortLabel: "Camping y cabaña",
    title: "Camping y fin de semana en cabaña",
    tagline: "Tienda, olla, provisiones — ¿quién lleva qué a la montaña o al lago?",
    intro:
      "Fines de semana en cabaña, camping y senderismo son grupos pequeños y densos. Equipaje limitado — una lista clara evita tres ollas y nada de café.",
    typical: "Normalmente entre 4 y 10 personas.",
    examples: [
      "Senderismo de fin de semana en la montaña",
      "Cabaña de invierno / esquí",
      "Camping familiar",
      "Retiro de grupo juvenil",
    ],
    itemIdeas: [
      "Tienda(s) / esterillas",
      "Saco extra",
      "Hornillo / gas",
      "Olla / sartén",
      "Cafetera / French press",
      "Básicos (pasta, arroz, aceite)",
      "Desayuno (muesli, pan, Nutella)",
      "Snacks para rutas",
      "Linterna / botiquín",
      "Juegos (cartas, mesa)",
    ],
    tips: [
      "Separa equipamiento y comida — si no alguien duplica la compra.",
      "Rutas de varios días: posiciones separadas por día.",
      "Planifica basura: bolsas y quién se la lleva.",
    ],
  },
  "taufe-kommunion": {
    shortLabel: "Bautizo y comunión",
    title: "Bautizo, comunión y confirmación",
    tagline: "Celebraciones en familia — coordinación de regalos y recepción.",
    intro:
      "Celebraciones con familia y amigos. Muy popular: coordinar los regalos — cada uno escribe una idea, sin duplicados.",
    typical: "Normalmente entre 10 y 17 invitados.",
    examples: [
      "Bautizo con familia y padrinos",
      "Fiesta tras la comunión",
      "Brunch de confirmación",
      "Lista de regalos para bautizo o confirmación",
    ],
    itemIdeas: [
      "Aportaciones al buffet (ensaladas, tarta, antipasti)",
      "Cava o bebida sin alcohol de bienvenida",
      "Café y tarta por la tarde",
      "Decoración / velas / flores",
      "Libro de firmas",
      "Ideas de regalo (libro infantil, cadena de plata, juegos …)",
    ],
    tips: [
      "Crea la lista de regalos como evento propio — separada del catering.",
      "Tres generaciones a la mesa: que sea fácil de usar, enlace admin al organizador.",
      "Comentario para alergias (suele haber abuelos).",
    ],
  },
  karneval: {
    shortLabel: "Carnaval",
    title: "Carnaval, Fasching y Fastnacht",
    tagline: "Desfile, fiesta, brunch — ¿quién trae qué disfraz y qué buffet?",
    intro:
      "Los carnavales duran varios días con grupos cambiantes. Especialmente útil en los desayunos antes del desfile y el brunch de Rosenmontag.",
    typical: "Normalmente entre 10 y 22 personas.",
    examples: [
      "Desayuno de Gumpendonnerstag",
      "Brunch de carnaval o Rosenmontag",
      "Encuentro antes del desfile",
      "Desayuno de carnaval en guardería",
    ],
    itemIdeas: [
      "Accesorios de disfraz para intercambiar",
      "Panecillos / pan",
      "Licor / snacks de carnaval",
      "Donuts / Berlinesas",
      "Ponche infantil / vino caliente",
      "Bolsas para Kamelle (caramelos)",
    ],
    tips: [
      "Varios días — varias listas, o un evento con fechas en los ítems.",
      "Participantes del desfile: posiciones separadas antes y después.",
      "A mediodía se anima: fecha límite pronto (domingo de carnaval).",
    ],
  },
  einweihung: {
    shortLabel: "Inauguración",
    title: "Fiestas de mudanza e inauguración",
    tagline: "Casa nueva, estantes vacíos, invitados con hambre — caso claro para una lista compartida.",
    intro:
      "Inauguración de casa o piso, primera fiesta en la habitación nueva: cocina medio vacía, los invitados traen ensaladas, bebidas, a veces decoración.",
    typical: "Normalmente entre 6 y 15 invitados.",
    examples: [
      "Inauguración de piso o habitación",
      "Inauguración de casa con amigos",
      "Bienvenida al nuevo equipo o trabajo",
    ],
    itemIdeas: [
      "Ensaladas y dips",
      "Pizza / quiche",
      "Snacks",
      "Bebidas (vino, cerveza)",
      "Regalos de inauguración (planta, pan y sal, especias)",
      "Altavoz de música",
      "Vajilla (cocina aún incompleta)",
    ],
    tips: [
      "Añade regalos de invitados como posiciones — así no hay tres estanterías de velas.",
      "Una posición para ayudar a recoger al día siguiente — solo para los más cercanos.",
    ],
  },
  familienfest: {
    shortLabel: "Reunión familiar",
    title: "Reuniones familiares y aniversarios",
    tagline: "Tres generaciones, una lista — recetas antiguas y tecnología moderna en armonía.",
    intro:
      "Reuniones familiares — de cumpleaños de abuelos a reencuentros y aniversarios — suelen tener participantes de varios hogares. La lista ayuda sobre todo si la familia está repartida por el país.",
    typical: "Normalmente entre 20 y 45 personas.",
    examples: [
      "Fiesta de abuelos o boda de oro",
      "Reunión familiar de tres generaciones",
      "Cumpleaños redondo con familia extensa",
    ],
    itemIdeas: [
      "Recetas de siempre (la ensaladilla de la abuela)",
      "Tartas y bizcochos",
      "Buffet salado",
      "Bebidas para todas las edades",
      "Decoración y pared de fotos (antes y ahora)",
      "Playlist con canciones pedidas",
    ],
    tips: [
      "A los mayores: enlace admin por correo o en papel — no todos están en WhatsApp.",
      "Recetas tradicionales como ítems propios para que no se pierdan.",
      "Posición extra para la pared de fotos — une a toda la familia.",
    ],
  },
};

const CONTENT: ContentByLang = { de: DE, en: EN, es: ES };

export function getUseCaseContent(id: string, lang: string): UseCaseContent | undefined {
  const key = lang.slice(0, 2);
  return CONTENT[key]?.[id] ?? CONTENT.de[id];
}

export function getAllIds(): string[] {
  return Object.keys(DE);
}
